/**
 * Per-location admin page (feat-010-square-connect-admin).
 *
 * Hosts the Square Connection card for one Bespoke location. Other
 * per-location admin tiles can be added here as future features land.
 *
 * Route: /admin/locations/:locationId (wired in App.tsx under StaffRoute).
 * RBAC: viewable by Admin/Manager/Corporate (StaffRoute). Connect/Map/
 * Remove/Disconnect actions inside the card require Admin or Corporate.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import ScorecardNavigation from '../ScorecardNavigation';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import managementService from '../../../services/managementService';
import SquareConnectionCard from './SquareConnectionCard';
import PosEmployeeMappingCard from './PosEmployeeMappingCard';
import LocationLanguageDefaultCard from './LocationLanguageDefaultCard';

interface LocationLite {
  id: string;
  name: string;
  tenant_id?: string;
  /** feat-018-learn-i18n Phase 2: per-location default language (ADR 0004 D2).
   *  Optional — the list endpoint may not yet return it; defaults to 'en'. */
  default_language?: string;
}

function normalizeRole(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

// ADR 0001 D7 locked the post-callback flash convention: a single
// `square_oauth=<outcome>` param plus optional secondaries. Copy strings here
// are taken verbatim from the ADR's table so backend and frontend tell the
// same story.
// NOTE: `square_state` (fake-mode branch override consumed by
// squareConnectionService) is intentionally NOT stripped — it must persist
// across re-renders so QA can flip between the five UI branches.
const FLASH_PARAMS = [
  'square_oauth',
  'square_error',
  'merchant_id',
  'existing_merchant_id',
] as const;

function parseFlashFromQuery(
  params: URLSearchParams,
): { kind: 'success' | 'error'; text: string } | null {
  const outcome = params.get('square_oauth');
  if (!outcome) return null;
  switch (outcome) {
    case 'success':
      return { kind: 'success', text: 'Square account connected.' };
    case 'state_invalid':
      return {
        kind: 'error',
        text: 'That connection link expired or was already used. Please sign in and try again.',
      };
    case 'square_denied':
      return {
        kind: 'error',
        text: 'Square sign-in was cancelled. Try again when you’re ready.',
      };
    case 'exchange_failed': {
      const code = params.get('square_error') ?? 'unknown';
      return {
        kind: 'error',
        text: `Square didn’t accept the connection. Please try again. (Code: ${code})`,
      };
    }
    case 'tenant_already_connected':
      return {
        kind: 'error',
        text:
          'This tenant is already connected to a different Square account. Disconnect it first, then try again.',
      };
    case 'internal_error':
      return {
        kind: 'error',
        text:
          'Something went wrong on our end. Please try again in a minute, or contact support if it keeps failing.',
      };
    default:
      return { kind: 'error', text: 'Square connection failed.' };
  }
}

const LocationAdminView: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedTenant, tenantName } = useTenant();

  const [location, setLocation] = useState<LocationLite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const role = normalizeRole(user?.managementUser?.role);
  // Card-level write actions per charter: Admin OR Corporate.
  const canManage = role === 'Admin' || role === 'Corporate';

  // OAuth callback flash → handed to the card once, then stripped from URL
  // via history.replaceState per ADR 0001 D7.
  const initialFlash = useMemo(() => parseFlashFromQuery(searchParams), [searchParams]);
  useEffect(() => {
    if (FLASH_PARAMS.some((p) => searchParams.has(p))) {
      const next = new URLSearchParams(searchParams);
      FLASH_PARAMS.forEach((p) => next.delete(p));
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!locationId) {
          setError('No location selected.');
          return;
        }
        // Try tenant-scoped fetch first (auth-aware); fall back to /api/management/locations.
        let list: any[] | null = null;
        if (selectedTenant) {
          const r = await managementService.getLocationsByTenant(selectedTenant);
          if (r.success && r.locations) list = r.locations;
        }
        if (!list) {
          const r = await managementService.getLocations();
          if (r.success && r.locations) list = r.locations;
        }
        if (cancelled) return;
        const found = (list ?? []).find((l: any) => String(l.id ?? l.location_id) === String(locationId));
        if (!found) {
          setError(`Location ${locationId} not found in your tenant.`);
          return;
        }
        setLocation({
          id: String(found.id ?? found.location_id),
          name: found.name ?? found.location_name ?? `Location ${locationId}`,
          tenant_id: found.tenant_id ?? selectedTenant ?? undefined,
          default_language: found.default_language ?? undefined,
        });
      } catch (e: any) {
        if (cancelled) return;
        console.error('LocationAdminView - load failed', e);
        setError(e?.message ?? 'Failed to load location');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [locationId, selectedTenant]);

  const tenantId = location?.tenant_id ?? selectedTenant ?? '';

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <button
          onClick={() => navigate('/admin/locations')}
          className="inline-flex items-center gap-1.5 text-sm text-inkMuted hover:text-ink mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Locations
        </button>

        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-display font-semibold text-ink">
              {loading ? 'Location' : location?.name ?? `Location ${locationId}`}
            </h1>
            <p className="text-sm text-inkMuted">
              Per-location admin · <span className="font-medium text-ink">{tenantName}</span>
            </p>
          </div>
          {!loading && location && (
            <button
              data-testid="location-coverage-link"
              onClick={() => navigate(`/admin/locations/${location.id}/coverage`)}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 border border-softBorder rounded bg-white hover:bg-cream text-ink self-start sm:self-auto"
            >
              <Users className="w-4 h-4" />
              Staff coverage
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            {error}
          </div>
        )}

        {!loading && location && tenantId && (
          <>
            <SquareConnectionCard
              bespokeLocationId={location.id}
              bespokeLocationName={location.name}
              tenantId={tenantId}
              canManage={canManage}
              initialFlash={initialFlash}
            />
            <PosEmployeeMappingCard
              tenantId={tenantId}
              bespokeLocationId={location.id}
              canManage={canManage}
            />
            <LocationLanguageDefaultCard
              tenantId={tenantId}
              locationId={location.id}
              initialLanguage={location.default_language}
              canManage={canManage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default LocationAdminView;
